import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isTomorrow, startOfDay, addDays, parseISO, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabaseClient';
import tankerkoenigService from '../services/tankerkoenigService';

const { 
  FiClock, FiCalendar, FiShoppingCart, FiCheckSquare, 
  FiDroplet, FiRefreshCw, FiAlertCircle, FiHeart, 
  FiArrowLeft, FiActivity, FiTrash2, FiHome, FiUtensils,
  FiZap, FiMapPin
} = FiIcons;

const TabletView = () => {
  const { state } = useFamilyContext();
  const { familyId } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [fuelPrices, setFuelPrices] = useState([]);
  const [isLoadingFuel, setIsLoadingFuel] = useState(false);

  // Uhrzeit-Update jede Sekunde
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Spritpreise laden
  const fetchFuel = async () => {
    if (!familyId) return;
    setIsLoadingFuel(true);
    try {
      const { data: settings } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'tankerkoenig_config')
        .eq('family_id', familyId)
        .maybeSingle();

      if (settings?.value?.enabled && settings.value.apiKey) {
        const result = await tankerkoenigService.getNearbyStations(
          settings.value.lat, settings.value.lng, settings.value.radius,
          settings.value.fuelType || 'e5', settings.value.apiKey
        );
        if (result.success) setFuelPrices(result.stations);
      }
    } catch (err) {
      console.error("Fehler beim Laden der Spritpreise:", err);
    } finally {
      setIsLoadingFuel(false);
    }
  };

  useEffect(() => {
    fetchFuel();
    const interval = setInterval(fetchFuel, 1000 * 60 * 30); // Alle 30 Min
    return () => clearInterval(interval);
  }, [familyId]);

  const ensureDate = (dateVal) => {
    if (!dateVal) return null;
    const d = typeof dateVal === 'string' ? parseISO(dateVal) : dateVal;
    return isValid(d) ? d : null;
  };

  // Aggregierte Events für die nächsten 7 Tage
  const upcomingEvents = useMemo(() => {
    const today = startOfDay(new Date());
    const next7Days = addDays(today, 7);
    return [
      ...(state.appointments || []).map(a => ({ ...a, type: 'appointment', date: ensureDate(a.date), icon: FiClock, color: 'text-blue-400' })),
      ...(state.events || []).map(e => ({ ...e, type: 'event', date: ensureDate(e.date), icon: FiCalendar, color: 'text-indigo-400' })),
      ...(state.householdPlans || []).map(h => ({ ...h, type: 'household', date: ensureDate(h.dueDate), icon: FiHome, color: 'text-emerald-400' })),
      ...(state.trashCalendar || []).map(t => ({ ...t, type: 'trash', date: ensureDate(t.date), icon: FiTrash2, color: 'text-rose-400' })),
    ].filter(item => item.date && item.date >= today && item.date <= next7Days)
     .sort((a, b) => a.date - b.date);
  }, [state]);

  const shoppingItemsCount = state.shoppingItems?.filter(i => !i.completed).length || 0;
  const todoItemsCount = state.todos?.filter(t => !t.completed).length || 0;

  return (
    <div className="h-screen bg-slate-950 text-slate-100 flex flex-col p-6 overflow-hidden gap-6 font-sans">
      
      {/* HEADER: UHRZEIT & DATUM */}
      <header className="flex justify-between items-center px-4 shrink-0">
        <div>
          <div className="flex items-baseline gap-4">
            <h1 className="text-8xl font-black tracking-tighter text-white tabular-nums">
              {format(currentTime, 'HH:mm')}
            </h1>
            <span className="text-4xl font-light text-slate-500 tabular-nums w-12">
              {format(currentTime, 'ss')}
            </span>
          </div>
          <p className="text-3xl text-blue-400 font-medium">
            {format(currentTime, 'EEEE, d. MMMM yyyy', { locale: de })}
          </p>
        </div>
        
        {/* Familien-Member Status */}
        <div className="flex -space-x-4">
          {state.familyMembers?.map(m => (
            <div 
              key={m.id} 
              className="w-16 h-16 rounded-2xl border-4 border-slate-950 flex items-center justify-center text-2xl font-bold shadow-2xl relative group"
              style={{ backgroundColor: m.color + '20', color: m.color, borderColor: '#020617' }}
            >
              {m.avatar || m.name[0]}
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-500 border-4 border-slate-950 rounded-full"></div>
            </div>
          ))}
        </div>
      </header>

      {/* MAIN CONTENT AREA: SPLIT SCREEN */}
      <main className="flex-1 flex gap-6 min-h-0">
        
        {/* LINKS: KALENDER (7 TAGE) */}
        <section className="flex-1 bg-slate-900/40 rounded-[2.5rem] border border-white/5 p-8 flex flex-col min-h-0 shadow-inner">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-4xl font-bold flex items-center gap-4">
              <SafeIcon icon={FiCalendar} className="text-blue-500" />
              Kalender <span className="text-slate-500 font-light text-2xl">(nächste 7 Tage)</span>
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto pr-4 space-y-4 custom-scrollbar">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, idx) => (
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  key={idx} 
                  className="bg-slate-800/40 p-6 rounded-3xl flex items-center gap-6 border border-white/5 hover:bg-slate-800/60 transition-colors"
                >
                  <div className={`w-20 h-20 rounded-2xl flex flex-col items-center justify-center text-white shadow-lg ${
                    isToday(event.date) ? 'bg-blue-600 scale-110 shadow-blue-900/40' : 'bg-slate-700'
                  }`}>
                    <span className="text-xs font-bold uppercase opacity-80">{format(event.date, 'MMM')}</span>
                    <span className="text-3xl font-black">{format(event.date, 'dd')}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <SafeIcon icon={event.icon} className={`${event.color} text-2xl`} />
                      <p className="text-3xl font-bold truncate">{event.title}</p>
                    </div>
                    <div className="flex gap-4 text-xl text-slate-400 mt-1">
                       {event.time && <span className="font-mono">{event.time} Uhr</span>}
                       <span className="uppercase tracking-widest text-slate-500 font-bold text-sm">
                         {isToday(event.date) ? 'HEUTE' : isTomorrow(event.date) ? 'MORGEN' : format(event.date, 'EEEE', { locale: de })}
                       </span>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-slate-600">
                <SafeIcon icon={FiZap} className="text-6xl mb-4 opacity-20" />
                <p className="text-2xl italic">Keine Termine geplant</p>
              </div>
            )}
          </div>
        </section>

        {/* RECHTS: SPRITPREISE & QUICK STATS */}
        <section className="w-[450px] flex flex-col gap-6">
          
          {/* SPRITPREISE */}
          <div className="bg-slate-900/40 rounded-[2.5rem] border border-white/5 p-8 flex flex-col shadow-inner">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold flex items-center gap-3">
                <SafeIcon icon={FiDroplet} className="text-amber-500" />
                Spritpreise
              </h2>
              <button 
                onClick={fetchFuel} 
                className={`p-2 rounded-full hover:bg-white/5 transition-colors ${isLoadingFuel ? 'animate-spin' : ''}`}
              >
                <SafeIcon icon={FiRefreshCw} />
              </button>
            </div>
            
            <div className="space-y-4">
              {fuelPrices.length > 0 ? (
                fuelPrices.slice(0, 3).map((station, idx) => (
                  <div key={idx} className="bg-slate-800/60 p-5 rounded-3xl border border-white/5 flex justify-between items-center">
                    <div>
                      <p className="text-2xl font-black text-white truncate max-w-[180px]">{station.brand}</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <SafeIcon icon={FiMapPin} /> {station.dist} km • {station.isOpen ? 'Geöffnet' : 'Geschlossen'}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-4xl font-mono font-bold text-amber-400">
                        {station.price?.toFixed(2)}<span className="text-xl">€</span>
                      </div>
                      <p className="text-xs text-slate-500 uppercase font-bold tracking-widest">Super E5</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-slate-500 italic text-center py-4">Keine Daten verfügbar</p>
              )}
            </div>
          </div>

          {/* QUICK STATS (EINKAUF & TASKS) */}
          <div className="flex-1 grid grid-rows-2 gap-6 min-h-0">
            
            {/* EINKAUF TILE */}
            <div className="bg-blue-600 rounded-[2.5rem] p-8 flex items-center justify-between shadow-lg shadow-blue-900/20 relative overflow-hidden group">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white/80 uppercase tracking-widest mb-1">Einkauf</h3>
                <p className="text-7xl font-black text-white">{shoppingItemsCount}</p>
                <p className="text-xl text-blue-200 mt-2 font-medium">Artikel auf der Liste</p>
              </div>
              <SafeIcon icon={FiShoppingCart} className="text-[10rem] text-white/10 absolute -right-8 -bottom-8 rotate-12 group-hover:scale-110 transition-transform" />
            </div>

            {/* AUFGABEN TILE */}
            <div className="bg-emerald-600 rounded-[2.5rem] p-8 flex items-center justify-between shadow-lg shadow-emerald-900/20 relative overflow-hidden group">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold text-white/80 uppercase tracking-widest mb-1">Aufgaben</h3>
                <p className="text-7xl font-black text-white">{todoItemsCount}</p>
                <p className="text-xl text-emerald-200 mt-2 font-medium">Offene Todos</p>
              </div>
              <SafeIcon icon={FiCheckSquare} className="text-[10rem] text-white/10 absolute -right-8 -bottom-8 rotate-12 group-hover:scale-110 transition-transform" />
            </div>

          </div>
        </section>
      </main>

      {/* FOOTER: SYSTEM INFO */}
      <footer className="flex justify-between items-center px-6 text-slate-600 text-sm font-medium">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> System Online</span>
          <span>•</span>
          <span>Letztes Update: {format(currentTime, 'HH:mm:ss')}</span>
        </div>
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiHome} />
          <span>Lorzens Home Dashboard v2.0</span>
        </div>
      </footer>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 20px; }
        @keyframes pulse-soft {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default TabletView;